import { Box, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

interface ShimmerOverlayProps {
  isAnimating: boolean;
  children: ReactNode;
  duration?: number;
  sx?: SxProps<Theme>;
}

/**
 * A reusable shimmer overlay component that creates a diagonal
 * light sweep animation over its children (like ChatGPT's thinking effect).
 *
 * Usage:
 * ```tsx
 * <ShimmerOverlay isAnimating={isLoading}>
 *   <YourContent />
 * </ShimmerOverlay>
 * ```
 */
export const ShimmerOverlay = ({
  isAnimating,
  children,
  duration = 1.2,
  sx,
}: ShimmerOverlayProps) => {
  // Total cycle time: sweep duration + 100ms pause
  const totalCycle = duration + 0.1;
  // Percentage of cycle spent animating vs pausing
  const sweepPercentage = (duration / totalCycle) * 100;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...sx,
        '&::before': isAnimating
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
              pointerEvents: 'none',
              // Two shimmer lines using background-size to repeat
              background: `linear-gradient(
                120deg,
                transparent 0%,
                transparent 30%,
                rgba(255, 255, 255, 0.2) 40%,
                rgba(255, 255, 255, 0.5) 50%,
                rgba(255, 255, 255, 0.2) 60%,
                transparent 70%,
                transparent 100%
              )`,
              backgroundSize: '50% 100%',
              backgroundRepeat: 'repeat-x',
              // Linear timing as per design, with pause at end
              animation: `shimmerSweep ${totalCycle}s linear infinite`,
              '@keyframes shimmerSweep': {
                '0%': {
                  backgroundPosition: '-100% 0',
                },
                [`${sweepPercentage}%`]: {
                  backgroundPosition: '200% 0',
                },
                '100%': {
                  backgroundPosition: '200% 0',
                },
              },
              // Respect reduced motion preferences
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
            }
          : undefined,
      }}
    >
      {children}
    </Box>
  );
};

export default ShimmerOverlay;
